import { AuthKitProvider, useAuth as useWorkOSAuth } from '@workos-inc/authkit-react';
import { ReactNode, useEffect, useCallback } from 'react';

import { api } from './api';
import { globalConfig } from './configuration/config';

interface WorkOSAuthProviderProps {
  children: ReactNode;
}

/**
 * WorkOS AuthKit Provider wrapper that handles:
 * - OAuth callback interception
 * - Token refresh
 * - API token synchronization
 */
export const WorkOSAuthProvider = ({ children }: WorkOSAuthProviderProps)=> {
  const WORKOS_CLIENT_ID = globalConfig.config.workosClientId;

  return (
    <AuthKitProvider
      clientId={WORKOS_CLIENT_ID}
      devMode={globalConfig.config.environment === 'development'}
      onRedirectCallback={() => {
        // Clean up URL after OAuth callback
        window.history.replaceState({}, document.title, window.location.pathname);
      }}
    >
      {children}
    </AuthKitProvider>
  );
}

/**
 * Hook for accessing WorkOS auth state and methods.
 * Syncs access token with the API client.
 */
export const useAuth = () => {
  const auth = useWorkOSAuth();
  const { getAccessToken, user, isLoading } = auth;

  // Sync token with API on auth state changes
  useEffect(() => {
    const syncToken = async () => {
      if (user && !isLoading) {
        try {
          const token = await getAccessToken();

          if (token) {
            api.updateTokenInHeaders(token);
          }
        } catch (error) {
          console.error('Failed to get access token:', error);
        }
      }
    };

    syncToken();
  }, [user, isLoading, getAccessToken]);

  // Wrapper to get fresh token for API calls
  const getToken = useCallback(async (): Promise<string | undefined> => {
    try {
      return await getAccessToken();
    } catch (error) {
      console.error('Failed to get access token:', error);
      return undefined;
    }
  }, [getAccessToken]);

  return {
    ...auth,
    getToken,
  };
}

/**
 * Hook to handle the /login route.
 * Automatically triggers sign-in when navigating to /login.
 */
export function useLoginRoute() {
  const { signIn } = useWorkOSAuth();

  useEffect(() => {
    if (window.location.pathname === '/login') {
      signIn();
    }
  }, [signIn]);
}

export { useWorkOSAuth };
